// =============================================================================
// PTEL - PendingRequests v1
// FB.10.09 · Lista de solicitudes pendientes de rol Editor con Aprobar/Rechazar
// Se integra dentro del AdminPanel como sección colapsable
// =============================================================================

import { useState } from 'react';
import {
  Clock, CheckCircle2, XCircle, ChevronDown, ChevronRight, User,
} from 'lucide-react';
import { ROLE_DURATIONS, type RoleDuration, type EditorRequest } from '@/types/roles';
import type { AuthTokenSet } from '@/components/auth/auth.tokens';
import { useIsMounted } from '@/hooks/useIsMounted';

// =============================================================================
// Types
// =============================================================================

export interface PendingRequestsProps {
  requests: EditorRequest[];
  resolvedRequests?: EditorRequest[];
  loading: boolean;
  onApprove: (requestId: string, duration: RoleDuration) => Promise<void>;
  onReject: (requestId: string, reason?: string) => Promise<void>;
  t: AuthTokenSet;
  /** Duraciones que se ofrecen al aprobar (por defecto, todas). SEC0-b2c DOP-20 (a). */
  durations?: readonly RoleDuration[];
}

// =============================================================================
// Sub-component: RequestCard
// =============================================================================

interface RequestCardProps {
  request: EditorRequest;
  onApprove: (requestId: string, duration: RoleDuration) => Promise<void>;
  onReject: (requestId: string, reason?: string) => Promise<void>;
  t: AuthTokenSet;
  allowed?: readonly RoleDuration[];
}

function RequestCard({ request, onApprove, onReject, t, allowed }: RequestCardProps) {
  const [selectedDuration, setSelectedDuration] = useState<RoleDuration>('3m');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  // SSCA-FIX-1: guard de montaje para los setState tras await de handleApprove/handleReject.
  const isMounted = useIsMounted();

  const durations = (Object.entries(ROLE_DURATIONS) as [RoleDuration, typeof ROLE_DURATIONS[RoleDuration]][])
    .filter(([key]) => !allowed || allowed.includes(key));

  const handleApprove = async () => {
    if (!request.id || busy) return;
    setBusy(true);
    try {
      await onApprove(request.id, selectedDuration);
    } finally {
      if (isMounted()) setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!request.id || busy) return;
    setBusy(true);
    try {
      await onReject(request.id, rejectReason || undefined);
    } finally {
      if (isMounted()) {
        setBusy(false);
        setRejecting(false);
      }
    }
  };

  const timeAgo = getTimeAgo(request.requestedAt);

  return (
    <div
      data-testid={`request-card-${request.id}`}
      style={{
        padding: '12px', background: t.bgInput,
        border: `1px solid ${t.border}`, borderRadius: '8px',
        marginBottom: '8px',
      }}
    >
      {/* Header: user info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: '#3b82f620', color: '#3b82f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: 700, flexShrink: 0,
        }}>
          <User size={14} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '13px', color: t.text }}>
            {request.displayName}
          </div>
          <div style={{ fontSize: '11px', color: t.textMuted }}>
            {request.email} · <Clock size={10} style={{ verticalAlign: 'middle' }} /> {timeAgo}
          </div>
        </div>
      </div>

      {/* Reason */}
      <div style={{
        fontSize: '12px', color: t.textSecondary, lineHeight: 1.5,
        padding: '8px', background: `${t.border}40`, borderRadius: '6px',
        marginBottom: '10px', fontStyle: 'italic',
      }}>
        «{request.reason}»
      </div>

      {/* Reject form */}
      {rejecting ? (
        <div style={{ marginBottom: '10px' }}>
          <input
            data-testid={`reject-reason-${request.id}`}
            type="text"
            placeholder="Motivo del rechazo (opcional)…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{
              width: '100%', padding: '6px 8px', boxSizing: 'border-box',
              background: t.bgCard, border: `1px solid ${t.border}`,
              borderRadius: '4px', color: t.text, fontSize: '12px',
              fontFamily: "'JetBrains Mono', monospace", marginBottom: '6px',
            }}
          />
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button
              data-testid={`reject-cancel-${request.id}`}
              onClick={() => { setRejecting(false); setRejectReason(''); }}
              style={{
                padding: '4px 10px', fontSize: '11px', background: 'transparent',
                border: `1px solid ${t.border}`, borderRadius: '4px',
                color: t.textMuted, cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              data-testid={`reject-confirm-${request.id}`}
              onClick={() => void handleReject()}
              disabled={busy}
              style={{
                padding: '4px 10px', fontSize: '11px', background: '#ef4444',
                border: 'none', borderRadius: '4px', color: '#fff',
                cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
              }}
            >
              Confirmar rechazo
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Duration selector */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '11px', color: t.textMuted, marginBottom: '4px' }}>
              Duración si se aprueba
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {durations.map(([key, meta]) => {
                const isActive = selectedDuration === key;
                return (
                  <button
                    key={key}
                    data-testid={`req-duration-${request.id}-${key}`}
                    onClick={() => setSelectedDuration(key)}
                    style={{
                      padding: '3px 8px', borderRadius: '4px', fontSize: '11px',
                      fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
                      border: `1px solid ${isActive ? '#3b82f6' : t.border}`,
                      background: isActive ? '#3b82f615' : 'transparent',
                      color: isActive ? '#3b82f6' : t.textMuted,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button
              data-testid={`reject-btn-${request.id}`}
              onClick={() => setRejecting(true)}
              disabled={busy}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '5px 10px', fontSize: '12px', background: 'transparent',
                border: `1px solid #ef444440`, borderRadius: '6px',
                color: '#ef4444', cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              <XCircle size={13} /> Rechazar
            </button>
            <button
              data-testid={`approve-btn-${request.id}`}
              onClick={() => void handleApprove()}
              disabled={busy}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '5px 10px', fontSize: '12px', background: '#22c55e',
                border: 'none', borderRadius: '6px', color: '#fff',
                cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 600,
                fontFamily: "'Space Grotesk', sans-serif",
                opacity: busy ? 0.6 : 1,
              }}
            >
              <CheckCircle2 size={13} /> Aprobar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Sub-component: ResolvedCard
// =============================================================================

interface ResolvedCardProps {
  request: EditorRequest;
  t: AuthTokenSet;
}

function ResolvedCard({ request, t }: ResolvedCardProps) {
  const isApproved = request.status === 'approved';
  const color = isApproved ? '#22c55e' : '#ef4444';
  const label = isApproved ? 'Aprobado' : 'Rechazado';
  const actor = request.reviewedBy ?? '—';
  const fecha = request.reviewedAt
    ? request.reviewedAt.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div
      data-testid={`resolved-card-${request.id}`}
      style={{
        padding: '10px 12px',
        background: t.bgInput,
        border: `1px solid ${t.border}`,
        borderRadius: '8px',
        marginBottom: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: t.text }}>
          {request.displayName}
        </div>
        <div style={{ fontSize: '11px', color: t.textMuted }}>
          {request.email}
        </div>
      </div>
      <div
        data-testid={`resolved-badge-${request.id}`}
        style={{
          fontSize: '11px', fontWeight: 700,
          color: color,
          border: `1px solid ${color}40`,
          borderRadius: '6px',
          padding: '2px 8px',
          fontFamily: "'JetBrains Mono', monospace",
          whiteSpace: 'nowrap',
        }}
      >
        {label} por {actor} · {fecha}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PendingRequests({ requests, resolvedRequests = [], loading, onApprove, onReject, t, durations }: PendingRequestsProps) {
  const [expanded, setExpanded] = useState(true);
  const [resolvedExpanded, setResolvedExpanded] = useState(false);
  const count = requests.length;

  if (loading) {
    return (
      <div data-testid="pending-loading" style={{
        fontSize: '12px', color: t.textMuted, padding: '8px 0',
      }}>
        Cargando solicitudes…
      </div>
    );
  }

  if (count === 0 && resolvedRequests.length === 0) return null;

  return (
    <div data-testid="pending-requests" style={{ marginBottom: '16px' }}>
      {/* Collapsible header */}
      <button
        data-testid="pending-toggle"
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
          padding: '8px 0', background: 'none', border: 'none',
          cursor: 'pointer', color: t.text,
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{
          fontSize: '13px', fontWeight: 600,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          Solicitudes pendientes
        </span>
        <span
          data-testid="pending-badge"
          style={{
            background: '#f59e0b', color: '#000', fontSize: '11px',
            fontWeight: 700, padding: '1px 7px', borderRadius: '10px',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {count}
        </span>
      </button>

      {/* List */}
      {expanded && (
        <div data-testid="pending-list">
          {requests.map(req => (
            <RequestCard
              key={req.id}
              request={req}
              onApprove={onApprove}
              allowed={durations}
              onReject={onReject}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Sección Resueltas */}
      {resolvedRequests.length > 0 && (
        <div data-testid="resolved-section" style={{ marginTop: '12px' }}>
          <button
            data-testid="resolved-toggle"
            onClick={() => setResolvedExpanded(!resolvedExpanded)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
              padding: '8px 0', background: 'none', border: 'none',
              cursor: 'pointer', color: t.text,
            }}
          >
            {resolvedExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span style={{ fontSize: '13px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>
              Solicitudes resueltas
            </span>
            <span style={{
              background: '#6b728020', color: t.textMuted, fontSize: '11px',
              fontWeight: 700, padding: '1px 7px', borderRadius: '10px',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {resolvedRequests.length}
            </span>
          </button>
          {resolvedExpanded && (
            <div data-testid="resolved-list">
              {resolvedRequests.map(req => (
                <ResolvedCard key={req.id} request={req} t={t} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function getTimeAgo(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}
